import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Link2, Share2, Trash2, Check, Loader2, Plus } from 'lucide-react';
import { apiRequest } from '../config/api';
import { useOAuthPopup } from '../hooks/useOAuthPopup';
import { useToast } from '../context/ToastContext';

const PROVIDER_ICONS = {
  google: Mail,
};

function StatusDot({ status }) {
  const colors = {
    connected: 'bg-green-500',
    revoked: 'bg-red-500',
    error: 'bg-red-500',
    expired: 'bg-yellow-500',
  };
  return <div className={`w-2 h-2 rounded-full ${colors[status] || 'bg-gray-400'}`} />;
}

export default function IntegrationsTab() {
  const { showSuccess, showError } = useToast();
  const [providers, setProviders] = useState({});
  const [connections, setConnections] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [loadingConnections, setLoadingConnections] = useState(true);
  const [togglingShare, setTogglingShare] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const loadConnections = useCallback(async () => {
    try {
      const resp = await apiRequest('/connections');
      if (resp.ok) {
        const data = await resp.json();
        setConnections(data.items || []);
      }
    } catch (err) {
      console.error('Error loading connections:', err);
    } finally {
      setLoadingConnections(false);
    }
  }, []);

  const loadProviders = useCallback(async () => {
    try {
      const resp = await apiRequest('/connections/providers');
      if (resp.ok) {
        const data = await resp.json();
        setProviders(data);
      }
    } catch (err) {
      console.error('Error loading providers:', err);
    } finally {
      setLoadingProviders(false);
    }
  }, []);

  useEffect(() => {
    loadProviders();
    loadConnections();
  }, [loadProviders, loadConnections]);

  const { connect } = useOAuthPopup({
    onSuccess: () => {
      showSuccess('Account connected successfully');
      loadConnections();
    },
    onError: (err) => {
      showError(err || 'Failed to connect account');
    },
  });

  const handleToggleShare = async (connection) => {
    setTogglingShare(connection.id);
    try {
      const resp = await apiRequest(`/connections/${connection.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_shared_with_org: !connection.is_shared_with_org }),
      });
      if (resp.ok) {
        const updated = await resp.json();
        setConnections((prev) => prev.map((c) => c.id === updated.id ? updated : c));
        showSuccess(updated.is_shared_with_org ? 'Connection shared with organization' : 'Connection unshared');
      } else {
        const err = await resp.json().catch(() => ({}));
        showError(err.detail || 'Failed to update sharing');
      }
    } catch {
      showError('Failed to update sharing');
    } finally {
      setTogglingShare(null);
    }
  };

  const handleDelete = async (connectionId) => {
    if (!window.confirm('Are you sure you want to disconnect this account?')) return;
    setDeletingId(connectionId);
    try {
      const resp = await apiRequest(`/connections/${connectionId}`, { method: 'DELETE' });
      if (resp.ok) {
        setConnections((prev) => prev.filter((c) => c.id !== connectionId));
        showSuccess('Account disconnected');
      } else {
        const err = await resp.json().catch(() => ({}));
        showError(err.detail || 'Failed to disconnect');
      }
    } catch {
      showError('Failed to disconnect account');
    } finally {
      setDeletingId(null);
    }
  };

  const isLoading = loadingProviders || loadingConnections;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Integrations</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Connect third-party accounts to use in workflows and automations.</p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          <span className="ml-2 text-sm text-gray-400">Loading integrations...</span>
        </div>
      ) : (
        <>
          {/* Available Providers */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <Link2 className="h-4 w-4 text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Available Integrations</h3>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(providers).map(([key, provider]) => {
                  const Icon = PROVIDER_ICONS[key] || Link2;
                  const connectedCount = connections.filter((c) => c.provider === key && c.status === 'connected').length;
                  return (
                    <div
                      key={key}
                      className="flex items-center gap-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{provider.name}</p>
                        {connectedCount > 0 && (
                          <p className="text-xs text-green-600 dark:text-green-400">{connectedCount} connected</p>
                        )}
                      </div>
                      <button
                        onClick={() => connect(key)}
                        className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-1.5"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Connect
                      </button>
                    </div>
                  );
                })}
                {Object.keys(providers).length === 0 && (
                  <p className="text-sm text-gray-400 col-span-full text-center py-4">No integrations available yet.</p>
                )}
              </div>
            </div>
          </div>

          {/* Connected Accounts */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Connected Accounts ({connections.length})
                </h3>
              </div>
            </div>
            {connections.length === 0 ? (
              <div className="p-6 text-center">
                <Link2 className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                <p className="text-sm text-gray-500 dark:text-gray-400">No accounts connected yet.</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Connect an account above to get started.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50 dark:divide-gray-700/50">
                {connections.map((conn) => {
                  const Icon = PROVIDER_ICONS[conn.provider] || Link2;
                  const meta = conn.metadata_json || {};
                  const isWorkspace = meta.account_type === 'workspace' || !!meta.hd;
                  return (
                    <div key={conn.id} className="px-6 py-4 flex items-center gap-4">
                      <div className="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {meta.picture ? (
                          <img src={meta.picture} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Icon className="w-4 h-4 text-gray-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {conn.display_name || conn.account_identifier}
                          </p>
                          <StatusDot status={conn.status} />
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {conn.account_identifier}
                          </span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                            isWorkspace
                              ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                          }`}>
                            {isWorkspace ? `Workspace: ${meta.hd}` : 'Personal'}
                          </span>
                          {!conn.is_owner && meta.name && (
                            <span className="text-[10px] text-gray-400 italic">(Shared by {meta.name})</span>
                          )}
                        </div>
                      </div>
                      {conn.is_owner && (
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <div className="flex items-center gap-1.5">
                            <Share2 className="w-3.5 h-3.5 text-gray-400" />
                            <button
                              type="button"
                              disabled={togglingShare === conn.id}
                              onClick={() => handleToggleShare(conn)}
                              aria-pressed={conn.is_shared_with_org}
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                                conn.is_shared_with_org ? 'bg-brand-600' : 'bg-gray-200 dark:bg-gray-600'
                              } ${togglingShare === conn.id ? 'opacity-50' : ''}`}
                            >
                              <span
                                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                                  conn.is_shared_with_org ? 'translate-x-[18px]' : 'translate-x-[3px]'
                                }`}
                              />
                            </button>
                          </div>
                          <button
                            onClick={() => handleDelete(conn.id)}
                            disabled={deletingId === conn.id}
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors disabled:opacity-50"
                          >
                            {deletingId === conn.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
